import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Trophy, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const mcqQuestions = [
  {
    id: 1,
    pillar: '義 Giustizia',
    question: 'Quale comportamento rappresenta meglio la Giustizia in un ristorante?',
    options: [
      'Prezzi trasparenti e proporzionati alla qualità offerta',
      'Menu con prezzi nascosti fino al conto finale',
      'Prezzi variabili in base all\'aspetto del cliente',
      'Offerte speciali solo per clienti abituali'
    ],
    correct: 0
  },
  {
    id: 2,
    pillar: '勇 Coraggio',
    question: 'Come si manifesta il Coraggio culinario?',
    options: [
      'Servire sempre gli stessi piatti tradizionali',
      'Sperimentare nuove tecniche rispettando gli ingredienti',
      'Copiare i piatti di ristoranti famosi',
      'Usare solo ingredienti costosi per impressionare'
    ],
    correct: 1
  },
  {
    id: 3,
    pillar: '仁 Compassione',
    question: 'Quale atteggiamento dimostra Compassione verso gli ospiti?',
    options: [
      'Ignorare le richieste speciali per allergie',
      'Adattare i piatti alle esigenze alimentari specifiche',
      'Servire porzioni ridotte per aumentare i profitti',
      'Affrettare i clienti per liberare i tavoli'
    ],
    correct: 1
  },
  {
    id: 4,
    pillar: '礼 Rispetto',
    question: 'Il Rispetto in cucina si esprime attraverso:',
    options: [
      'L\'uso esclusivo di ingredienti importati',
      'La valorizzazione delle tradizioni locali e stagionalità',
      'L\'imposizione di un unico stile culinario',
      'La critica aperta verso altre cucine'
    ],
    correct: 1
  },
  {
    id: 5,
    pillar: '誠 Onestà',
    question: 'Quale pratica rappresenta l\'Onestà culinaria?',
    options: [
      'Descrivere i piatti in modo fantasioso per attirare clienti',
      'Indicare chiaramente origine e metodi di preparazione',
      'Nascondere l\'uso di ingredienti surgelati',
      'Promettere tempi di attesa irrealistici'
    ],
    correct: 1
  },
  {
    id: 6,
    pillar: '忠義 Devozione',
    question: 'La Devozione culinaria si riconosce da:',
    options: [
      'Cambiare continuamente menu per seguire le mode',
      'Mantenere standard qualitativi costanti nel tempo',
      'Ridurre la qualità per aumentare i margini',
      'Delegare completamente la cucina ad altri'
    ],
    correct: 1
  },
  {
    id: 7,
    pillar: '清 Purezza',
    question: 'La Purezza degli ingredienti significa:',
    options: [
      'Usare solo prodotti biologici certificati',
      'Scegliere ingredienti di qualità e trattarli con rispetto',
      'Utilizzare esclusivamente prodotti locali',
      'Evitare qualsiasi tipo di condimento'
    ],
    correct: 1
  },
  {
    id: 8,
    pillar: 'Metodologia',
    question: 'Nel sistema BushiFood, il BushiScore viene calcolato:',
    options: [
      'Solo in base al prezzo del ristorante',
      'Attraverso la media ponderata dei 7 Pilastri',
      'Esclusivamente sulla presentazione dei piatti',
      'In base al numero di stelle Michelin'
    ],
    correct: 1
  }
];

const scenarioQuestions = [
  {
    id: 9,
    title: 'Conflitto Menu/Origine',
    scenario: 'Un ristorante pubblicizza "Pasta fresca fatta in casa" ma scopri che usa pasta industriale. Il piatto è comunque buono. Come valuti questo aspetto?',
    pillar: '誠 Onestà'
  },
  {
    id: 10,
    title: 'Inclusione/Allergie',
    scenario: 'Un cliente con celiachia chiede informazioni sui piatti senza glutine. Il cameriere non sa rispondere e il cuoco si rifiuta di adattare i piatti. Come influisce questo sulla tua valutazione?',
    pillar: '仁 Compassione'
  },
  {
    id: 11,
    title: 'Innovazione vs Stagionalità',
    scenario: 'Un chef propone un piatto fusion innovativo ma usa ingredienti fuori stagione importati da lontano. Come bilanci Coraggio e Rispetto per la stagionalità?',
    pillar: '勇 Coraggio / 礼 Rispetto'
  },
  {
    id: 12,
    title: 'Disciplina vs Cura',
    scenario: 'Un ristorante ha regole rigide (no modifiche ai piatti, orari fissi) ma offre un servizio impeccabile e cibo eccellente. Come valuti Devozione e Compassione?',
    pillar: '忠義 Devozione / 仁 Compassione'
  }
];

export default function QuizPage() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: any }>({});
  const [showResults, setShowResults] = useState(false);
  const [timeLeft, setTimeLeft] = useState(720); // 12 minuti in secondi
  const [quizStarted, setQuizStarted] = useState(false);

  const totalQuestions = mcqQuestions.length + scenarioQuestions.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;

  const startQuiz = () => {
    setQuizStarted(true);
    // Avvia il timer
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleMCQAnswer = (questionId: number, answerIndex: number) => {
    setAnswers({ ...answers, [questionId]: answerIndex });
  };

  const handleScenarioAnswer = (questionId: number, answer: string) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const nextQuestion = () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmitQuiz();
    }
  };

  const handleSubmitQuiz = () => {
    setShowResults(true);
  };

  const calculateScore = () => {
    let correctMCQ = 0;
    mcqQuestions.forEach((q) => {
      if (answers[q.id] === q.correct) {
        correctMCQ++;
      }
    });
    
    // Per gli scenari, assumiamo che risposte non vuote valgano 1 punto ciascuna
    let scenarioPoints = 0;
    scenarioQuestions.forEach((q) => {
      if (answers[q.id] && answers[q.id].trim().length > 50) {
        scenarioPoints++;
      }
    });
    
    return correctMCQ + scenarioPoints;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!quizStarted) {
    return (
      <div className="min-h-screen py-12 px-4">
        <div className="container mx-auto max-w-3xl">
          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto mb-4">
                <Trophy className="h-16 w-16 text-accent" />
              </div>
              <CardTitle className="font-bushido text-4xl mb-4">
                Quiz <span className="gold-accent">Accreditamento</span>
              </CardTitle>
              <CardDescription className="text-lg">
                Diventa un Recensore Accreditato BushiFood
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-secondary">12</div>
                  <div className="text-sm text-muted-foreground">Domande Totali</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-secondary">12</div>
                  <div className="text-sm text-muted-foreground">Minuti Tempo</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-secondary">10/12</div>
                  <div className="text-sm text-muted-foreground">Soglia Minima</div>
                </div>
              </div>

              <div className="space-y-4 text-left">
                <h3 className="font-semibold text-lg">Struttura del Quiz:</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Badge variant="secondary">8 MCQ</Badge>
                    <span className="text-sm">Domande a scelta multipla sui Pilastri</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="secondary">4 Scenari</Badge>
                    <span className="text-sm">Situazioni pratiche da analizzare</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-left">
                <h3 className="font-semibold text-lg">Cosa Otterrai:</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Badge "Recensore Accreditato"</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">CredScore iniziale 60/100</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Accesso a funzionalità avanzate</span>
                  </div>
                </div>
              </div>

              <Button onClick={startQuiz} size="lg" className="btn-samurai px-12 py-3">
                <Trophy className="mr-2 h-5 w-5" />
                Inizia Quiz
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showResults) {
    const score = calculateScore();
    const passed = score >= 10;
    const credScore = passed ? 60 : 30;

    return (
      <div className="min-h-screen py-12 px-4">
        <div className="container mx-auto max-w-3xl">
          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto mb-4">
                {passed ? (
                  <CheckCircle className="h-16 w-16 text-green-500" />
                ) : (
                  <AlertCircle className="h-16 w-16 text-red-500" />
                )}
              </div>
              <CardTitle className="font-bushido text-4xl mb-4">
                {passed ? 'Congratulazioni!' : 'Quiz Non Superato'}
              </CardTitle>
              <CardDescription className="text-lg">
                {passed 
                  ? 'Sei ora un Recensore Accreditato BushiFood'
                  : 'Puoi riprovare il quiz dopo 24 ore'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className={`text-3xl font-bold ${passed ? 'text-green-500' : 'text-red-500'}`}>
                    {score}/12
                  </div>
                  <div className="text-sm text-muted-foreground">Punteggio Finale</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className={`text-3xl font-bold ${passed ? 'text-green-500' : 'text-orange-500'}`}>
                    {credScore}/100
                  </div>
                  <div className="text-sm text-muted-foreground">CredScore Iniziale</div>
                </div>
              </div>

              {passed && (
                <div className="space-y-4">
                  <Badge className="bg-green-600 text-white px-6 py-2 text-lg">
                    <span className="font-kanji mr-2">認定</span>
                    Recensore Accreditato
                  </Badge>
                  <p className="text-sm text-muted-foreground">
                    Il tuo badge è stato attivato. Ora puoi scrivere recensioni con maggiore credibilità 
                    e accedere a funzionalità esclusive della piattaforma BushiFood.
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {passed ? (
                  <>
                    <Button className="btn-samurai">
                      Scrivi Prima Recensione
                    </Button>
                    <Button variant="outline" className="btn-honor">
                      Vai al Profilo
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" className="btn-honor">
                    Studia i Pilastri
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const isScenarioQuestion = currentQuestion >= mcqQuestions.length;
  const currentMCQ = !isScenarioQuestion ? mcqQuestions[currentQuestion] : null;
  const currentScenario = isScenarioQuestion ? scenarioQuestions[currentQuestion - mcqQuestions.length] : null;

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="container mx-auto max-w-3xl">
        {/* Header con Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h1 className="font-bushido text-2xl font-bold">
              Quiz Accreditamento
            </h1>
            <div className="flex items-center space-x-4">
              <Badge variant="secondary">
                <Clock className="mr-1 h-4 w-4" />
                {formatTime(timeLeft)}
              </Badge>
              <Badge variant="outline">
                {currentQuestion + 1}/{totalQuestions}
              </Badge>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Domanda MCQ */}
        {currentMCQ && (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3 mb-4">
                <Badge variant="secondary">{currentMCQ.pillar}</Badge>
                <Badge variant="outline">Scelta Multipla</Badge>
              </div>
              <CardTitle className="text-xl leading-relaxed">
                {currentMCQ.question}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={answers[currentMCQ.id]?.toString()}
                onValueChange={(value) => handleMCQAnswer(currentMCQ.id, parseInt(value))}
              >
                {currentMCQ.options.map((option, index) => (
                  <div key={index} className="flex items-center space-x-2 p-3 rounded-lg hover:bg-muted/50">
                    <RadioGroupItem value={index.toString()} id={`option-${index}`} />
                    <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
        )}

        {/* Domanda Scenario */}
        {currentScenario && (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3 mb-4">
                <Badge variant="secondary">{currentScenario.pillar}</Badge>
                <Badge variant="outline">Scenario</Badge>
              </div>
              <CardTitle className="text-xl mb-4">
                {currentScenario.title}
              </CardTitle>
              <CardDescription className="text-base leading-relaxed bg-muted/50 p-4 rounded-lg">
                {currentScenario.scenario}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="scenario-answer">
                  La tua analisi (minimo 50 caratteri):
                </Label>
                <Textarea
                  id="scenario-answer"
                  value={answers[currentScenario.id] || ''}
                  onChange={(e) => handleScenarioAnswer(currentScenario.id, e.target.value)}
                  placeholder="Analizza la situazione applicando i principi del Bushidō..."
                  className="min-h-[120px]"
                />
                <div className="text-xs text-muted-foreground text-right">
                  {(answers[currentScenario.id] || '').length} caratteri
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
            disabled={currentQuestion === 0}
          >
            Precedente
          </Button>
          
          <Button
            onClick={nextQuestion}
            disabled={
              (currentMCQ && answers[currentMCQ.id] === undefined) ||
              (currentScenario && (!answers[currentScenario.id] || answers[currentScenario.id].length < 50))
            }
            className="btn-samurai"
          >
            {currentQuestion === totalQuestions - 1 ? 'Completa Quiz' : 'Successiva'}
          </Button>
        </div>
      </div>
    </div>
  );
}